import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { X, ShoppingCart, Trash2, Landmark, ArrowRight, ChevronLeft, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { formatDimensions, formatUSD } from '../utils/format';
import { COUNTRIES } from '../utils/countries';
import { FRAME_OPTIONS, getFrameLabel } from './FrameSelector';
import './Cart.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PayPalIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="currentColor">
    <path d="M7.3 21.4h-3a.4.4 0 0 1-.4-.5l2.6-16.3a.5.5 0 0 1 .5-.5h6.5c2.5 0 4.5.6 5.3 2 .5 1 .6 2 .3 3.4-.7 3.4-3 4.6-6.4 4.6h-2c-.5 0-.9.3-1 .8l-.6 3.5-.3 1.6c0 .2-.2.4-.4.4Zm5.4-15h-.7l-1 6h.6c2 0 3.7-.4 4.1-2.5.2-.8 0-1.5-.4-2-.5-.8-1.4-1.5-2.6-1.5Z"/>
  </svg>
);

export const Cart = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // items -> details (contact + shipping + method) -> redirect to /order/{ref}
  const [step, setStep] = useState('items');
  const [paymentMethods, setPaymentMethods] = useState({
    paypal: { enabled: false, link: '' },
    transfer: { enabled: true },
  });
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'MX',
    payment_method: 'paypal',
  });
  const [shippingQuote, setShippingQuote] = useState({ amount: 0, label: '', quote_required: false });
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState({ valid: false, percent: 0 });
  const [discountError, setDiscountError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const { cart, removeFromCart, getCartTotal, getCartCount, clearCart } = useCart();
  const { t, lang } = useLanguage();

  // Auto-load saved newsletter discount code on mount
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cl_newsletter_v1') || 'null');
      if (stored && stored.code) {
        setDiscountCode(stored.code);
      }
    } catch (_e) {
      // ignore invalid JSON
    }
  }, []);

  // Validate the discount code whenever it changes
  useEffect(() => {
    setDiscountError('');
    setDiscountInfo({ valid: false, percent: 0 });
    const code = (discountCode || '').trim().toUpperCase();
    if (!code) return;
    const tm = setTimeout(() => {
      fetch(`${BACKEND_URL}/api/discount/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then(async (r) => {
          if (!r.ok) {
            const data = await r.json().catch(() => ({}));
            throw new Error(data.detail || 'Invalid code');
          }
          return r.json();
        })
        .then((d) => setDiscountInfo({ valid: true, percent: d.percent || 0 }))
        .catch((e) => setDiscountError(e.message));
    }, 350);
    return () => clearTimeout(tm);
  }, [discountCode]);

  const subtotal = getCartTotal();
  const discountAmount = discountInfo.valid ? Math.round(subtotal * discountInfo.percent) / 100 : 0;
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/payments/methods`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setPaymentMethods(data);
        if (!data?.paypal?.enabled) {
          setForm((p) => ({ ...p, payment_method: 'transfer' }));
        }
      })
      .catch(() => {});
  }, []);

  // Quote shipping every time country changes
  useEffect(() => {
    if (!form.country) return;
    fetch(`${BACKEND_URL}/api/shipping/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ country: form.country }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setShippingQuote({ amount: data.amount || 0, label: data.label || '', quote_required: !!data.quote_required }))
      .catch(() => {});
  }, [form.country]);

  const grandTotal = discountedSubtotal + (shippingQuote.amount || 0);
  const shippingQuoteRequired = !!shippingQuote.quote_required;

  const open = () => {
    setIsOpen(true);
    setStep('items');
    setErrorMsg('');
  };

  const close = () => {
    setIsOpen(false);
    setStep('items');
  };

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const submitOrder = async (e) => {
    e?.preventDefault();
    setErrorMsg('');
    // Basic validation
    if (cart.length === 0) return;
    const required = ['customer_name', 'customer_email', 'line1', 'city', 'postal_code', 'country'];
    for (const k of required) {
      if (!String(form[k] || '').trim()) {
        setErrorMsg(lang === 'es' ? 'Por favor completa los campos obligatorios' : 'Please fill the required fields');
        return;
      }
    }
    setIsProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map((item) => ({ id: String(item.id), title: item.title, price: item.price, frame: item.frame || null })),
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          notes: form.notes,
          discount_code: discountInfo.valid ? discountCode : '',
          shipping_address: {
            name: form.customer_name,
            line1: form.line1,
            line2: form.line2,
            city: form.city,
            state: form.state,
            postal_code: form.postal_code,
            country: form.country,
          },
          payment_method: form.payment_method,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Order failed');
      }
      const data = await response.json();
      clearCart();
      window.location.href = `/order/${data.reference}`;
    } catch (error) {
      setErrorMsg(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const paypalEnabled = paymentMethods?.paypal?.enabled;

  return (
    <>
      <button className="cart-button" onClick={open} data-testid="cart-open-btn" style={{ display: isOpen ? 'none' : 'flex' }}>
        <ShoppingCart size={24} />
        {getCartCount() > 0 && <span className="cart-count">{getCartCount()}</span>}
      </button>

      {isOpen && (
        <div className="cart-overlay" onClick={close}>
          <div className="cart-panel" onClick={(e) => e.stopPropagation()}>
            <div className="cart-header">
              {step !== 'items' ? (
                <button className="cart-back" onClick={() => setStep('items')} data-testid="cart-back-btn">
                  <ChevronLeft size={20} /> {t('cart_title')}
                </button>
              ) : (
                <h2>{t('cart_title')}</h2>
              )}
              <button className="cart-close" onClick={close}>
                <X size={24} />
              </button>
            </div>

            <div className="cart-content">
              {cart.length === 0 ? (
                <div className="cart-empty">
                  <ShoppingCart size={48} />
                  <p>{t('cart_empty')}</p>
                </div>
              ) : step === 'items' ? (
                <>
                  <div className="cart-items">
                    {cart.map((item) => (
                      <div key={item.id} className="cart-item">
                        <img src={item.image} alt={item.title} className="cart-item-image" />
                        <div className="cart-item-details">
                          <h3 translate="no">{item.title}</h3>
                          <p className="cart-item-meta" translate="no">{formatDimensions(item.dimensions, lang)}</p>
                          {item.frame && (() => {
                            const opt = FRAME_OPTIONS.find((o) => o.id === item.frame);
                            if (!opt) return null;
                            return (
                              <span className="frame-pill">
                                <span className="frame-pill-dot" style={{ background: opt.color }} />
                                {getFrameLabel(item.frame, lang)}
                              </span>
                            );
                          })()}
                          <p className="cart-item-price" translate="no">{formatUSD(item.price)} USD</p>
                        </div>
                        <button className="cart-item-remove" onClick={() => removeFromCart(item.id)} data-testid={`remove-${item.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="cart-footer">
                    <div className="cart-total">
                      <span>{t('cart_total')}</span>
                      <span className="cart-total-amount" translate="no">{formatUSD(getCartTotal())} USD</span>
                    </div>
                    <Button className="checkout-button" onClick={() => setStep('details')} data-testid="cart-checkout-btn">
                      {t('cart_checkout')} <ArrowRight size={16} style={{ marginLeft: 6 }} />
                    </Button>
                    <button className="clear-cart" onClick={clearCart} data-testid="cart-clear-btn">
                      {t('cart_clear')}
                    </button>
                  </div>
                </>
              ) : (
                <form className="checkout-form" onSubmit={submitOrder}>
                  <div className="pay-methods-header">
                    <span className="pay-methods-eyebrow">{lang === 'es' ? '— Pago' : '— Checkout'}</span>
                    <h3 className="pay-methods-heading">{t('pay_transfer_form_title')}</h3>
                  </div>

                  {/* Contact */}
                  <div className="form-section">
                    <span className="form-section-title">{lang === 'es' ? 'Contacto' : 'Contact'}</span>
                    <label className="form-label">
                      <span className="form-label-text">{t('form_name')} *</span>
                      <input type="text" required value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} data-testid="form-name" />
                    </label>
                    <div className="form-row">
                      <label className="form-label">
                        <span className="form-label-text">{t('form_email')} *</span>
                        <input type="email" required value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} data-testid="form-email" />
                      </label>
                      <label className="form-label">
                        <span className="form-label-text">{t('form_phone')}</span>
                        <input type="tel" value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)} data-testid="form-phone" />
                      </label>
                    </div>
                  </div>

                  {/* Shipping */}
                  <div className="form-section">
                    <span className="form-section-title">{lang === 'es' ? 'Dirección de envío' : 'Shipping address'}</span>
                    <label className="form-label">
                      <span className="form-label-text">{t('ship_country')} *</span>
                      <select required value={form.country} onChange={(e) => update('country', e.target.value)} data-testid="form-country">
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{lang === 'es' ? c.es : c.en}</option>
                        ))}
                      </select>
                    </label>
                    <label className="form-label">
                      <span className="form-label-text">{t('ship_address')} *</span>
                      <input type="text" required value={form.line1} onChange={(e) => update('line1', e.target.value)} data-testid="form-line1" />
                    </label>
                    <label className="form-label">
                      <span className="form-label-text">{t('ship_address2')}</span>
                      <input type="text" value={form.line2} onChange={(e) => update('line2', e.target.value)} />
                    </label>
                    <div className="form-row">
                      <label className="form-label">
                        <span className="form-label-text">{t('ship_city')} *</span>
                        <input type="text" required value={form.city} onChange={(e) => update('city', e.target.value)} data-testid="form-city" />
                      </label>
                      <label className="form-label">
                        <span className="form-label-text">{t('ship_state')}</span>
                        <input type="text" value={form.state} onChange={(e) => update('state', e.target.value)} />
                      </label>
                    </div>
                    <label className="form-label">
                      <span className="form-label-text">{t('ship_postal')} *</span>
                      <input type="text" required value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)} data-testid="form-postal" />
                    </label>
                  </div>

                  {/* Totals */}
                  <div className="pay-totals">
                    <div className="pay-totals-row">
                      <span>{t('ship_subtotal')}</span>
                      <span translate="no">{formatUSD(subtotal)} USD</span>
                    </div>

                    {/* Discount input + applied discount row */}
                    <div className="discount-input-row">
                      <input
                        type="text"
                        placeholder={lang === 'es' ? 'Código de descuento' : 'Discount code'}
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        data-testid="discount-input"
                      />
                      {discountInfo.valid && (
                        <span className="discount-badge">−{discountInfo.percent}%</span>
                      )}
                    </div>
                    {discountError && discountCode && (
                      <p className="discount-error">{lang === 'es' ? 'Código no válido' : 'Invalid code'}</p>
                    )}
                    {discountInfo.valid && discountAmount > 0 && (
                      <div className="pay-totals-row pay-totals-discount">
                        <span>{lang === 'es' ? `Descuento (${discountCode})` : `Discount (${discountCode})`}</span>
                        <span translate="no">−{formatUSD(discountAmount)} USD</span>
                      </div>
                    )}

                    <div className="pay-totals-row">
                      <span>{t('ship_shipping')}</span>
                      <span translate="no">
                        {shippingQuoteRequired
                          ? t('ship_quote')
                          : (shippingQuote.amount > 0 ? `${formatUSD(shippingQuote.amount)} USD` : t('ship_free'))}
                      </span>
                    </div>
                    <div className="pay-totals-row pay-totals-grand">
                      <span>{shippingQuoteRequired ? t('ship_total_partial') : t('ship_total')}</span>
                      <span className="pay-summary-amount" translate="no">{formatUSD(grandTotal)} <span className="pay-summary-currency">USD</span></span>
                    </div>
                    {shippingQuoteRequired && (
                      <p className="ship-quote-note">{t('ship_quote_help')}</p>
                    )}
                  </div>

                  {/* Payment method */}
                  <div className="form-section">
                    <span className="form-section-title">{t('pay_choose_method')}</span>
                    <div className="pay-list">
                      {paypalEnabled && (
                        <label className={`pay-radio ${form.payment_method === 'paypal' ? 'active' : ''}`}>
                          <input type="radio" name="paymethod" value="paypal" checked={form.payment_method === 'paypal'} onChange={() => update('payment_method', 'paypal')} data-testid="pay-paypal-radio" />
                          <span className="pay-option-icon"><PayPalIcon size={18} /></span>
                          <span className="pay-option-text">
                            <span className="pay-option-title">{t('pay_paypal')}</span>
                            <span className="pay-option-desc">{t('pay_paypal_desc')}</span>
                          </span>
                        </label>
                      )}
                      <label className={`pay-radio ${form.payment_method === 'transfer' ? 'active' : ''}`}>
                        <input type="radio" name="paymethod" value="transfer" checked={form.payment_method === 'transfer'} onChange={() => update('payment_method', 'transfer')} data-testid="pay-transfer-radio" />
                        <span className="pay-option-icon"><Landmark size={18} strokeWidth={1.25} /></span>
                        <span className="pay-option-text">
                          <span className="pay-option-title">{t('pay_transfer')}</span>
                          <span className="pay-option-desc">{t('pay_transfer_desc')}</span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <Button type="submit" className="checkout-button" disabled={isProcessing} data-testid="submit-order-btn">
                    {isProcessing ? t('cart_processing') : t('submit_request')}
                  </Button>

                  <div className="pay-trust">
                    <ShieldCheck size={14} strokeWidth={1.5} />
                    <span>{lang === 'es'
                      ? 'Tus datos se mantienen privados y nunca se comparten'
                      : 'Your details are kept private and never shared'}</span>
                  </div>

                  {errorMsg && <p className="pay-error">{errorMsg}</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Cart;
